import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';
import { listUsers } from '@appic/lib/api/users';

type UserProfile = Awaited<ReturnType<typeof listUsers>>[number];
import { Loader2 } from 'lucide-react';

function getInitials(fullName: string | null, email: string): string {
  if (fullName && fullName.trim()) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  const local = email.split('@')[0] || '';
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return (local[0] || '?').toUpperCase();
}
import { useServiceLayerAuth } from '@/lib/ServiceLayerAuthProvider';
import { useLanguage } from '../../providers/LanguageContext'

function useImpersonationMenu() {
  const { role: domainRole } = useServiceLayerAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allowed = domainRole === 'superadmin' || domainRole === 'sales_manager';

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userList = await listUsers();
      setUsers(userList.filter(u => u.isActive !== false));
    } catch (err: unknown) {
      console.error('[ImpersonationDropdown] Failed to load users:', err);
      setError('Benutzer konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleImpersonate = useCallback((email: string, fullName?: string | null) => {
    try {
      const payload = { email, fullName: fullName || null };
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('scout.impersonation.v1', JSON.stringify(payload));
        window.location.reload();
      }
    } catch (e) {
      console.error('[ImpersonationDropdown] Failed to set impersonation:', e);
    }
  }, []);

  const handleStopImpersonation = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('scout.impersonation.v1');
        window.location.reload();
      }
    } catch (e) {
      console.error('[ImpersonationDropdown] Failed to stop impersonation:', e);
    }
  }, []);

  const isImpersonating = React.useMemo(() => {
    try {
      if (typeof window === 'undefined') return false;
      const raw = sessionStorage.getItem('scout.impersonation.v1');
      return !!raw;
    } catch {
      return false;
    }
  }, []);

  return { users, loading, error, isImpersonating, loadUsers, handleImpersonate, handleStopImpersonation, allowed };
}

/** Menu content for impersonation (user list + stop). Use inside DropdownMenuContent or DropdownMenuSubContent. */
export function ImpersonationMenuContent() {
  const { t } = useLanguage()
  const { users, loading, error, isImpersonating, loadUsers, handleImpersonate, handleStopImpersonation } = useImpersonationMenu();

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return (
    <>
      {isImpersonating && (
        <>
          <DropdownMenuItem
            onClick={handleStopImpersonation}
            className="text-sm font-medium"
          >
            Impersonation beenden
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      {loading && (
        <DropdownMenuItem disabled>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Lade Benutzer...
        </DropdownMenuItem>
      )}
      {error && (
        <DropdownMenuItem disabled className="text-xs text-danger">
          {error}
        </DropdownMenuItem>
      )}
      {!loading && !error && users.length === 0 && (
        <DropdownMenuItem disabled className="text-xs text-muted-foreground">
          Keine Benutzer gefunden
        </DropdownMenuItem>
      )}
      {!loading && !error && users.length > 0 && (
        <>
          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
            {t('ui.impersonation.selectUser')}
          </div>
          {users.map((user) => {
            const roleLabels: Record<string, string> = {
              field_sales: 'VAD',
              handelsvertreter: 'HV',
              sales_manager: 'Vertriebsleiter',
              superadmin: 'Admin',
            };
            const roleLabel = (user.role && roleLabels[user.role]) || user.role || '';
            return (
              <DropdownMenuItem
                key={user.id || user.email}
                onClick={() => handleImpersonate(user.email, user.fullName)}
                className="text-sm flex items-center gap-3"
              >
                <span
                  className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-xs font-medium bg-muted text-accent"
                  aria-hidden
                >
                  {getInitials(user.fullName ?? null, user.email)}
                </span>
                <div className="flex flex-col min-w-0">
                  <span className="text-foreground">
                    {user.fullName || user.email}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {roleLabel}
                  </span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </>
      )}
    </>
  );
}

const ImpersonationDropdown: React.FC = () => {
  const { t } = useLanguage()
  const [isOpen, setIsOpen] = useState(false);
  const { allowed } = useImpersonationMenu();

  if (!allowed) return null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-sm px-3 py-1.5 hover:bg-opacity-80 transition-colors focus-visible:ring-0 focus-visible:outline-none"
          aria-label={t('ui.impersonation.selectImpersonation')}
        >
          Impersonation
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-dropdown overflow-y-auto overscroll-contain">
        <ImpersonationMenuContent />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ImpersonationDropdown;



